import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { NgxSpinnerModule } from "ngx-spinner";

// Translator.
import { TranslateService, TranslatePipe } from '@ngx-translate/core';

// Models.
import { VW_SIESPService } from '../../models/RES/vwSIESPService';
import { SIESPFullService } from '../../models/RES/siespFullService';

// Components.
import { RequestTypeDialogComponent } from './request-type-dialog/request-type-dialog.component';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { ResultDetailsComponent } from '../result-details/result-details.component';
import { DatesBetweenDialogComponent } from '../excel-reports/dates-between-dialog/dates-between-dialog.component';

// Services.
import { SIESPServicesService } from '../../services/siespServices.service';
import { NgxSpinnerService } from "ngx-spinner";
import { ReportsService } from '../../services/reports.service';

// Material.
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatProgressBarModule } from '@angular/material/progress-bar';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
  standalone: true,
  imports: [
    MatFormFieldModule, 
    MatInputModule, 
    MatTableModule, 
    MatSortModule, 
    MatPaginatorModule,
    TranslatePipe,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatDialogModule,
    MatSnackBarModule,
    MatProgressSpinnerModule,
    CommonModule,
    MatProgressBarModule,
    NgxSpinnerModule
  ]
})
export class DashboardComponent implements OnInit {
  constructor(
    public translate: TranslateService,
    private spinner: NgxSpinnerService,
    private dialog: MatDialog,
    private api: SIESPServicesService,
    private rapi: ReportsService
  ) {}
  private _snackBar = inject(MatSnackBar);
  router = inject(Router);
  isLoading: boolean = false;

  dataSource = new MatTableDataSource<any>([]);
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  data: SIESPFullService[] = [];
  displayedColumns: string[] = ['id', 'requestType', 'clientdivisionname', 'name', 'status', 'progress', 'researchStartDate', 'score', 'expiration', 'actions'];

  ngOnInit() {
    this.loadData();
  }

  loadData() {
    this.spinner.show();
    this.api.GetSIESPServicesByCognitoUser().subscribe({
      next: (response) => {
        this.spinner.hide();
        if (response.success === 1) {
          this.data = response.data;

          this.dataSource.data = this.data;
          this.dataSource.paginator = this.paginator;
          this.dataSource.sort = this.sort;
        } else {
          this.openSnackBar(response.message);
        }
      },
      error: (error) => {
        this.spinner.hide();

        const statusCode = error.status;
        const errorMessage = error.error?.message || "An error occurred trying to connect to web services.";
  
        this.openSnackBar(`Error ${statusCode}: ${errorMessage}`);
        console.error(`Error ${statusCode}`, error);
      },
    });
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  openAddDialog(){
    const dialogRef = this.dialog.open(RequestTypeDialogComponent, {
      width: "30%",
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.api.CreateSIESPService(result).subscribe({
          next: (response) => {
            if (response.success === 1) {
              this.router.navigate(['/newRequest'], { queryParams: { ServiceId: response.data } });
            } else {
              this.openSnackBar(response.message);
            }
          },
          error: (error) => {
            const statusCode = error.status;
            const errorMessage = error.error?.message || "An error occurred trying to connect to web services.";
      
            this.openSnackBar(`Error ${statusCode}: ${errorMessage}`);
            console.error(`Error ${statusCode}`, error);
          },
        });
      }
    });
  }

  edit(data: SIESPFullService){
    this.router.navigate(['/newRequest'], { queryParams: { ServiceId: data.id } });
  }

  resultDetails(data: SIESPFullService){
    const dialogRef = this.dialog.open(ResultDetailsComponent, {
      maxWidth: '100%',
      data: data
    });
  }

  confirmDelete(data: VW_SIESPService): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {
        message: `¿Estás seguro de que deseas eliminar el servicio para el candidato ${data.personName}?`
      },
      disableClose: true
    });
  
    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.api.Disable(data.id).subscribe({
          next: (response) => {
            if (response.success === 1) {
              this.openSnackBar("Service deleted");
              this.loadData();
            } else {
              this.openSnackBar(response.message);
            }
          },
          error: (error) => {
            const statusCode = error.status;
            const errorMessage = error.error?.message || "An error occurred trying to connect to web services.";
      
            this.openSnackBar(`Error ${statusCode}: ${errorMessage}`);
            console.error(`Error ${statusCode}`, error);
          },
        });
      }
    });
  }

  sendToSIES(element: SIESPFullService){
    this.spinner.show();
    if (element.progress === 100 && element.status_Id === 2) {
      this.api.SendToSIES(element.id).subscribe({
        next: (response) => {
          this.spinner.hide();
          if (response.success === 1) {
            this.openSnackBar("Service sent!");
            this.loadData();
          } else {
            this.openSnackBar(response.message);
          }
        },
        error: (error) => {
          this.spinner.hide();
          const statusCode = error.status;
          const errorMessage = error.error?.message || "An error occurred trying to connect to web services.";
    
          this.openSnackBar(`Error ${statusCode}: ${errorMessage}`);
          console.error(`Error ${statusCode}`, error);
        },
      });
    } else {
      this.spinner.hide();
      console.error('Formulario incompleto para enviar');
    }
  }

  resendToSIES(element: SIESPFullService){
    this.spinner.show();
    if (element.progress === 100 && element.status_Id === 4 && element.result === 40 && element.updateOverResult) {
      this.api.ResendToSIES(element.id).subscribe({
        next: (response) => {
          this.spinner.hide();
          if (response.success === 1) {
            this.openSnackBar("Service sent!");
            this.loadData();
          } else {
            this.openSnackBar(response.message);
          }
        },
        error: (error) => {
          this.spinner.hide();
          const statusCode = error.status;
          const errorMessage = error.error?.message || "An error occurred trying to connect to web services.";
    
          this.openSnackBar(`Error ${statusCode}: ${errorMessage}`);
          console.error(`Error ${statusCode}`, error);
        },
      });
    } else {
      this.spinner.hide();
      console.error('Formulario incompleto para enviar');
    }
  }

  openDownloadDialog(){
    const dialogRef = this.dialog.open(DatesBetweenDialogComponent, {
      width: '400px',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.downloadReport(result.fromDate, result.toDate, result.divisions);
      }
    });
  }

  downloadReport(fromDate: Date, toDate: Date, divisions: number[]): void {
    this.spinner.show();
    this.rapi.DownloadFilledMVXTemplate(fromDate, toDate, divisions).subscribe({
      next: (response) => {
        this.spinner.hide();
        if (response.success === 1) {
          const blob = this.b64toBlob(response.data.documentoB64, response.data.tipoDoc);
          const url = window.URL.createObjectURL(blob);

          const link = document.createElement('a');
          link.href = url;
          link.download = 'MVX_Report.xlsx';
          link.click();

          window.URL.revokeObjectURL(url);
        } else {
          this.openSnackBar(response.message);
        }
      },
      error: (error) => {
        this.spinner.hide();

        const statusCode = error.status;
        const errorMessage = error.error?.message || "An error occurred trying to connect to web services.";
  
        this.openSnackBar(`Error ${statusCode}: ${errorMessage}`);
        console.error(`Error ${statusCode}`, error);
      }
    });
  }

  b64toBlob(b64Data: string, contentType: string): Blob {
    const sliceSize = 512;
    const byteCharacters = atob(b64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    return new Blob(byteArrays, { type: contentType });
  }

  openSnackBar(Message: string) {
    this._snackBar.open(Message, "Dismiss", { duration: 5000 });
  }
}
